import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ChemicalReportOrgDetail,
  ChemicalReportSummary,
  ChemicalReportAggregate,
  ChemicalReportFilterOptions,
  ChemicalPdfExportRequest,
  ChemicalPdfExportResponse
} from '../models/chemical-report.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Chemical Report Service (Admin)
 *
 * Service for managing chemical usage reports in the admin portal.
 * Communicates with /admin/chemical-reports endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class ChemicalReportService {
  private readonly apiUrl = `${environment.apiUrl}/admin/chemical-reports`;

  constructor(private http: HttpClient) {}

  /**
   * Get organization detail chemical usage report
   *
   * @param organizationId - Organization ID to get report for
   * @param startDate - Report start date (YYYY-MM-DD format)
   * @param endDate - Report end date (YYYY-MM-DD format)
   * @param chemicalId - Optional chemical ID to filter by specific chemical
   * @param droneId - Optional drone ID to scope report to specific drone
   * @returns Organization detail report with chemical usage and drill-downs
   */
  async getOrganizationDetail(
    organizationId: string,
    startDate: string,
    endDate: string,
    chemicalId?: string,
    droneId?: string
  ): Promise<ChemicalReportOrgDetail | null> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    if (chemicalId) {
      params.append('chemicalId', chemicalId);
    }

    if (droneId) {
      params.append('droneId', droneId);
    }

    const url = `${this.apiUrl}/organization/${organizationId}?${params.toString()}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ChemicalReportOrgDetail>>(url)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return null;
  }

  /**
   * Get cross-organization chemical usage summary
   *
   * @param startDate - Report start date (YYYY-MM-DD format)
   * @param endDate - Report end date (YYYY-MM-DD format)
   * @param manufacturer - Optional manufacturer filter for partner reporting
   * @returns Cross-organization summary with all organizations
   */
  async getSummary(
    startDate: string,
    endDate: string,
    manufacturer?: string
  ): Promise<ChemicalReportSummary | null> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    if (manufacturer) {
      params.append('manufacturer', manufacturer);
    }

    const url = `${this.apiUrl}/summary?${params.toString()}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ChemicalReportSummary>>(url)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return null;
  }

  /**
   * Get chemical aggregate report — flat table of all chemicals across all orgs
   */
  async getChemicalAggregate(
    startDate: string,
    endDate: string,
    manufacturer?: string
  ): Promise<ChemicalReportAggregate | null> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    if (manufacturer) {
      params.append('manufacturer', manufacturer);
    }

    const url = `${this.apiUrl}/chemical-aggregate?${params.toString()}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ChemicalReportAggregate>>(url)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return null;
  }

  /**
   * Get filter options for chemical reports
   *
   * @returns Available organizations, manufacturers, chemicals, and date range hints
   */
  async getFilters(): Promise<ChemicalReportFilterOptions> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<ChemicalReportFilterOptions>>(`${this.apiUrl}/filters`)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      organizations: [],
      manufacturers: [],
      chemicals: [],
      dateRangeHints: {
        minDate: null,
        maxDate: null
      }
    };
  }

  /**
   * Export chemical usage report to PDF
   *
   * @param reportType - Type of report (organization-detail or cross-organization-summary)
   * @param filters - Filters applied to the report
   * @param data - Report data to export
   * @returns PDF download URL
   */
  async exportPdf(
    reportType: 'organization-detail' | 'cross-organization-summary' | 'chemical-aggregate',
    filters: ChemicalPdfExportRequest['filters'],
    data: ChemicalPdfExportRequest['data']
  ): Promise<ChemicalPdfExportResponse | null> {
    const requestBody: ChemicalPdfExportRequest = {
      reportType,
      filters,
      data
    };

    const response = await firstValueFrom(
      this.http.post<ApiResponse<ChemicalPdfExportResponse>>(
        `${this.apiUrl}/export-pdf`,
        requestBody
      )
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to export PDF');
  }
}
